/**
 * Catalogues the member profile needs. All of them are readable by any signed-in user
 * (api/member-types, api/marital-status, api/allergies, api/disabilities, api/relationships and
 * api/locations/active), so they work in self mode too.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useMemberTypeService,
  useMaritalStatusService,
  useAllergyService,
  useDisabilityService,
  useRelationshipService,
  useLocationService,
} from '@actschurch/ccms-sdk';

const FIVE_MINUTES = 5 * 60 * 1000;

export const MEMBER_LOOKUP_KEYS = {
  types: ['members', 'lookups', 'types'] as const,
  maritalStatuses: ['members', 'lookups', 'marital-statuses'] as const,
  allergies: ['members', 'lookups', 'allergies'] as const,
  disabilities: ['members', 'lookups', 'disabilities'] as const,
  relationships: ['members', 'lookups', 'relationships'] as const,
  locations: ['members', 'lookups', 'locations'] as const,
};

export function useMemberTypes() {
  const service = useMemberTypeService();
  const query = useQuery({ queryKey: MEMBER_LOOKUP_KEYS.types, queryFn: () => service.getAll(), staleTime: FIVE_MINUTES });
  const options = useMemo(
    () => (query.data ?? []).map((t) => ({ value: String(t.typeID), label: t.name })),
    [query.data],
  );
  return { ...query, types: query.data ?? [], options };
}

export function useMaritalStatuses() {
  const service = useMaritalStatusService();
  const query = useQuery({ queryKey: MEMBER_LOOKUP_KEYS.maritalStatuses, queryFn: () => service.getAll(), staleTime: FIVE_MINUTES });
  const options = useMemo(
    () => (query.data ?? []).filter((s) => s.maritalStatusID > 0).map((s) => ({ value: String(s.maritalStatusID), label: s.name })),
    [query.data],
  );
  return { ...query, statuses: query.data ?? [], options };
}

export function useAllergyCatalogue(enabled = true) {
  const service = useAllergyService();
  return useQuery({ queryKey: MEMBER_LOOKUP_KEYS.allergies, queryFn: () => service.getAll(), staleTime: FIVE_MINUTES, enabled });
}

export function useDisabilityCatalogue(enabled = true) {
  const service = useDisabilityService();
  return useQuery({ queryKey: MEMBER_LOOKUP_KEYS.disabilities, queryFn: () => service.getAll(), staleTime: FIVE_MINUTES, enabled });
}

export function useRelationships(enabled = true) {
  const service = useRelationshipService();
  const query = useQuery({ queryKey: MEMBER_LOOKUP_KEYS.relationships, queryFn: () => service.getAll(), staleTime: FIVE_MINUTES, enabled });
  const options = useMemo(
    () => (query.data ?? []).map((r) => ({ value: String(r.relationshipID), label: r.name })),
    [query.data],
  );
  return { ...query, relationships: query.data ?? [], options };
}

/**
 * Active campuses from the Locations service. LocationID 0 ("Online Campus") is a real campus,
 * so option values are strings and "0" is a valid choice.
 */
export function useCampuses() {
  const service = useLocationService();
  const query = useQuery({ queryKey: MEMBER_LOOKUP_KEYS.locations, queryFn: () => service.getAllActive(), staleTime: FIVE_MINUTES });
  const options = useMemo(
    () =>
      [...(query.data ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((l) => ({ value: String(l.locationID), label: l.name })),
    [query.data],
  );
  const nameOf = useMemo(() => {
    const map = new Map<number, string>();
    for (const l of query.data ?? []) map.set(l.locationID, l.name);
    return (id: number | null | undefined) => (id === null || id === undefined ? null : map.get(id) ?? null);
  }, [query.data]);
  return { ...query, campuses: query.data ?? [], options, nameOf };
}
